<script lang="ts">
  import { onMount } from 'svelte';
  import { router } from './lib/router.svelte';
  import { session } from './lib/session.svelte';
  import { nest } from './lib/nest.svelte';
  import { capture } from './lib/capture.svelte';
  import { persistStorage } from './lib/pwa.svelte';
  import { theme } from './lib/theme.svelte';
  import TabBar from './lib/design/TabBar.svelte';
  import Splash from './lib/design/Splash.svelte';
  import UpdateToast from './lib/design/UpdateToast.svelte';
  import OfflineBar from './lib/design/OfflineBar.svelte';
  import Ask from './routes/Ask.svelte';
  import Read from './routes/Read.svelte';
  import Capture from './routes/Capture.svelte';
  import Settings from './routes/Settings.svelte';
  import Usage from './routes/Usage.svelte';
  import Connect from './routes/Connect.svelte';
  import Signup from './routes/Signup.svelte';
  import Page from './routes/Page.svelte';

  const route = $derived(router.current);

  // Mirror the theme flag onto <html> (`:root.dark`) so the palette flips.
  $effect(() => {
    document.documentElement.classList.toggle('dark', theme.dark);
  });

  onMount(() => {
    void session.load();
  });

  // The whole app is behind a signed-in, TOTP-complete session. Until the first
  // `/pwa/auth/session` resolves we show a splash rather than flashing Connect.
  const phase = $derived(!session.loaded ? 'loading' : session.ready ? 'app' : 'connect');

  const STALE_MS = 5 * 60_000;
  function syncIfStale() {
    if (!session.ready || !session.state.dropboxConnected) return;
    if (!nest.lastPulledAt || Date.now() - nest.lastPulledAt > STALE_MS) void nest.pull();
  }

  // Once signed in: fill nest + capture queue from cache, reconcile with Dropbox,
  // then keep both fresh on reconnect / focus / a slow timer.
  let bootstrapped = false;
  $effect(() => {
    if (phase !== 'app' || bootstrapped) return;
    bootstrapped = true;
    void capture.hydrate();
    void nest.hydrate().then(async () => {
      if (session.state.dropboxConnected) {
        await nest.pull();
        if (nest.count > 0) void persistStorage();
      }
    });
  });

  onMount(() => {
    const onOnline = () => {
      void capture.flush();
      syncIfStale();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void capture.flush();
        syncIfStale();
      }
    };
    const timer = setInterval(syncIfStale, STALE_MS);
    addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  });
</script>

<OfflineBar />
<UpdateToast />

{#if phase === 'loading'}
  <Splash />
{:else if phase === 'connect'}
  {#if route.path === '/signup'}
    <Signup />
  {:else}
    <Connect />
  {/if}
{:else}
  {#if route.path === '/read'}
    <Read />
  {:else if route.path === '/capture'}
    <Capture />
  {:else if route.path === '/settings'}
    <Settings />
  {:else if route.path === '/usage'}
    <Usage />
  {:else if route.path === '/page/:slug'}
    <Page slug={route.params.slug ?? ''} />
  {:else}
    <Ask />
  {/if}
  <TabBar />
{/if}
